import { service } from '@ember/service'
import Component from '@glimmer/component'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import type { IntlService } from 'ember-intl'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { StoriesWrittenTotalsDataItem } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-totals'

interface Args {
  summaryTitle: string
  data: StoriesWrittenTotalsDataItem
  title?: string
  grade?: string
  tooltip?: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class StoriesWrittenTotals extends Component<Signature> {
  @service intl!: IntlService

  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.DarkBlue

  readonly chartSpacing: ChartSpacing = [10, 10, 10, 10]

  get summaryStats(): { label: string; value: string }[] {
    const intl = this.intl
    const { summary } = this.args.data

    const totalLabel = intl.t('reporting.summaryTitles.storiesWrittenTotals.total')
    const studentsLabel = intl.t('reporting.summaryTitles.storiesWrittenTotals.students')
    const averageLabel = intl.t('reporting.summaryTitles.storiesWrittenTotals.average')

    const total = summary.total_count
    const studentCount = summary.student_count
    const average = summary.student_average

    return [
      { label: totalLabel, value: formatNumber(total, 1) },
      { label: studentsLabel, value: formatNumber(studentCount, 1) },
      { label: averageLabel, value: formatNumber(average, 1) },
    ]
  }

  get months(): string[] {
    const monthsData = this.args.data.months
    return monthsData.map((item) => item.month)
  }

  get columns(): SeriesColumnOptions[] {
    const monthsData = this.args.data.months
    const data = monthsData.map((monthData, index) => {
      return { x: index, y: monthData.count, monthData }
    })

    return [{ name: 'count', data, color: colors.oceanyBlue300, type: 'column' }]
  }

  xAxisMonthFormatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
    const monthAndYear = this.value
    return convertDateFormat(monthAndYear as string)
  }

  <template>
    <div data-test-giraffe-stories-written-totals class="relative h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <ChartInfoTooltip @tooltipContent={{@tooltip}} />
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.months}}
            @chartSpacing={{this.chartSpacing}}
            @xAxisLabelFormatter={{this.xAxisMonthFormatter}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default StoriesWrittenTotals
